import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import Card from "../card";
import InputField from "../form-fields/InputField";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './tenant.css';
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { UserLoginData } from "../../components/common/charts/Enums";
import { TenantService, TenantVM } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { URLS } from "../../constants/constants";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import SimpleSpinner from "../../components/loader";
import PhoneInput from "../form-fields/PhoneInput";
import EmailInput from "../form-fields/EmailInput";

type FormValues = {
  Id: Number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  unitId: string;
};

interface EditTenantProps {
  fetchData?: any;
}

const EditTenant: React.FC<EditTenantProps> = ({ fetchData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userId = Number(sessionStorage.getItem(UserLoginData.user_id));
  const [currentTenantId, setCurrentTenantId] = useState(0);

  const methods = useForm<FormValues>({
    defaultValues: {
      Id: 0,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      unitId: "",
    },
    mode: "onBlur",
  });

  const { handleSubmit } = methods;

  // Simplified fetch: Only gets Tenant Data
  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const tenantRes = await apiHelper(
        TenantService.getApiTenant1,
        { showNotification: false },
        id
      );

      const tenantData = tenantRes?.data?.success as TenantVM | undefined;

      if (tenantData) {
        setCurrentTenantId(tenantData.id);

        methods.reset({
          Id: tenantData.id,
          firstName: tenantData.firstName,
          lastName: tenantData.lastName,
          email: tenantData.email, // Kept in state for API requirement
          phoneNumber: tenantData.phoneNumber,
          unitId: tenantData.unitId?.toString() ?? "", // Kept in state for API requirement
        });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load tenant data",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, methods]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const onSubmit = useCallback(
    async (formData: FormValues) => {
      setIsLoading(true);
      try {
        // Only updates the Tenant Profile info
        const tenantResponse = await apiHelper(
          TenantService.putApiTenant,
          {
            successMessage: "Tenant Updated Successfully",
            showNotification: true,
          },
          {
            id: currentTenantId,
            userId: userId,
            // Updated fields
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: formData.phoneNumber,
            // Existing fields (required by API but not editable)
            email: formData.email,
            unitId: formData.unitId,
          }
        );

        if (tenantResponse?.data?.success) {
          methods.reset();
          fetchData?.();
          const referrer = location.state?.from || URLS.TENANTS;
          navigate(referrer, { state: { activeTab: location.state?.activeTab } });
        } else if (tenantResponse?.errors?.Email) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            message: `${tenantResponse.errors?.Email[0]}`,
            type: NotificationType.Failure,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, navigate, fetchData, currentTenantId, methods, location]
  );

  const onCancel = useCallback(() => navigate(-1), [navigate]);

  return (
    <>
      <div className="d-flex flex-column gap-3 full-page-content edit-tenant-container">
        <LeftArrowTopHeader 
          title="Edit Tenant"
          backPath={URLS.TENANTS}
          onCancel={onCancel}
        />
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
            <SimpleSpinner myClassName="Icon40 mt-5" />
          </div>
        ) : (
          <FormProvider {...methods}>
            <Card>
              <div className="row">
                <div className="col-12">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                       <div className="col-12 col-md-6 col-lg-4">
                        <InputField
                          name="firstName"
                          label="First Name"
                          required
                          placeholder="Enter First Name"
                          requiredMessage="First name is required"
                          onlyAlphabets={true}
                          validation={{
                            minLength: { value: 2, message: "First name must be at least 2 characters long" },
                            maxLength: { value: 20, message: "First name cannot exceed 20 characters" }
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-lg-4">
                        <InputField
                          name="lastName"
                          label="Last Name"
                          required
                          placeholder="Enter Last Name"
                          requiredMessage="Last name is required"
                          onlyAlphabets={true}
                          validation={{
                            minLength: { value: 2, message: "Last name must be at least 2 characters long" },
                            maxLength: { value: 20, message: "Last name cannot exceed 20 characters" }
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4">
                        <EmailInput
                          name="email"
                          label="Email Address"
                          required
                          requiredMessage="Email is required"
                          placeholder="Enter Email Address"
                          validation={{
                            maxLength: { value: 50, message: "Email cannot exceed 50 characters" }
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-lg-4">
                        <PhoneInput
                          name="phoneNumber"
                          label="Contact No"
                          required
                          requiredMessage="Phone number is required"
                          placeholder="Enter Contact No"
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </Card>

            <FixedFooter
              onCancel={onCancel}
              onSubmit={handleSubmit(onSubmit)}
              loading={isLoading}
              buttonLabel={"Save Changes"}
            />
          </FormProvider>
        )}
      </div>
    </>
  );
};

export default EditTenant;